/**
 * DEC-24 item 4: what an authority answered against what this service answered.
 *
 * THE NUMBER THIS PAGE EXISTS FOR is "an authority approved and this service
 * refused". From 05:00 to 07:37 on 08.10.2026 that happened 1,773 times on
 * prod. Every one was recorded, correctly, with its basis. The only way
 * anybody saw it was Lucian noticing the shape of a chart.
 *
 * COUNTS ONLY, AND NO ROW DETAIL. No identifier, no station, no correlation
 * id. This page answers a question about rates; a single authorization is read
 * on Decisions, behind the same role and with its own audit entry.
 *
 * TODAY BESIDE THE SAME HOURS OF THE PREVIOUS SEVEN DAYS, because a rate with
 * nothing next to it cannot show a change, and a change is the whole subject.
 * The comparison says how many days are behind each figure, so an average can
 * be read as an average: a sum alone renders one busy Tuesday and seven quiet
 * days identically.
 *
 * ONE DEFINITION WITH THE ALERT. The service sends the bases it counts as
 * "approved then refused", and they are the alert's own set. A reader holding
 * a mail beside this page is comparing one definition with itself.
 */

import { useEffect, useState } from 'react'
import {
  Caption1,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { DataTableShell, EmptyState, LoadingState, MonoText } from '../platform'
import { api } from '../api'
import type { RatioAnswer, RatioRow, ComparisonRow } from './ratioReading'
import { pairWithHistory, perDay, refusedShare } from './ratioReading'

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  note: { color: tokens.colorNeutralForeground3 },
  bad: {
    color: tokens.colorStatusDangerForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  bases: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    color: tokens.colorNeutralForeground3,
  },
})

/** An hour label in the reader's own zone, from an instant with an offset.
 *
 *  The service sends UTC with the offset on it and the browser converts. A
 *  naive string would be a question about whose clock that only the browser
 *  can answer, which this project has paid for twice.
 */
function hourLabel(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    timeZoneName: 'short',
  })
}

function share(value: number | null): string {
  // ABSENT, NOT ZERO. An hour where the authority approved nothing is an idle
  // hour, and "0.0%" says the lane was perfect.
  return value === null ? 'nothing to compare' : `${(value * 100).toFixed(1)}%`
}

export default function ApprovalRatio() {
  const styles = useStyles()
  const [data, setData] = useState<RatioAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<RatioAnswer>('/decisions/ratio')
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'the ratio cannot be read'))
  }, [])

  if (error) return <EmptyState title="The ratio cannot be read" description={error} />
  if (!data) return <LoadingState label="Counting decisions" />

  const now = data.windows.find((w) => w.window === 'now')
  const previous = data.windows.find((w) => w.window === 'previous')
  const rows = (now?.rows ?? []) as RatioRow[]
  const history = (previous && 'rows' in previous ? previous.rows : []) as ComparisonRow[]
  const paired = pairWithHistory(rows, history)

  if (paired.length === 0) {
    return (
      <EmptyState
        title="Nothing was decided in this window"
        description={`No authorization reached this service in the last ${data.hours} hours. That is not the same as everything being approved, which is why no rate is shown.`}
      />
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.bases}>
        <Caption1>
          Counts only. Per hour and per lane, in your own timezone, over the last{' '}
          {data.hours} hours, beside the same hours of the previous{' '}
          {data.comparison_days} days.
        </Caption1>
        <Caption1>
          An authority approved and this service refused is counted from{' '}
          <MonoText>{data.approved_then_refused_bases.join(', ')}</MonoText>, which is
          the same set the alert uses, so a mail and this page cannot disagree.
        </Caption1>
      </div>

      <DataTableShell>
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Hour</TableHeaderCell>
              <TableHeaderCell>Lane</TableHeaderCell>
              <TableHeaderCell>Decisions</TableHeaderCell>
              <TableHeaderCell>Authority approved</TableHeaderCell>
              <TableHeaderCell>We approved</TableHeaderCell>
              <TableHeaderCell>We refused an approval</TableHeaderCell>
              <TableHeaderCell>Same hour, previous days</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paired.map(({ row, history: was }) => {
              const bad = row.approved_then_refused > 0
              return (
                <TableRow key={`${row.hour}:${row.lane}`}>
                  <TableCell>{hourLabel(row.hour)}</TableCell>
                  <TableCell>{row.lane}</TableCell>
                  <TableCell>{row.total}</TableCell>
                  <TableCell>{row.authority_approved}</TableCell>
                  <TableCell>{row.we_approved}</TableCell>
                  <TableCell>
                    <Text className={bad ? styles.bad : undefined}>
                      {row.approved_then_refused} ({share(refusedShare(row))})
                    </Text>
                  </TableCell>
                  <TableCell>
                    {was === null ? (
                      <Caption1 className={styles.note}>no earlier day</Caption1>
                    ) : (
                      <Caption1 className={styles.note}>
                        {perDay(was)?.toFixed(1)} decisions a day over {was.days}{' '}
                        {was.days === 1 ? 'day' : 'days'},{' '}
                        {was.approved_then_refused} refused approvals
                      </Caption1>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </DataTableShell>
    </div>
  )
}
